<script lang="ts">
  import { getSetting, SETTING_USER_PHONE } from '$lib/db/settings';
  import { formatPhone } from '$lib/format';
  import { t } from '$lib/i18n/index.svelte';
  import type { Dict } from '$lib/i18n/en';
  import {
    addContactInitialState,
    confirmAddContact,
    normalizeNumber,
    startAddContact,
    type AddContactState,
  } from '$lib/protocol/registration';
  import { syncContact } from '$lib/protocol/events-loop';
  import { markFreshAdd } from '$lib/state/push-nudge';

  import { currentRoute, goBack, navigate } from '../../app/router.svelte';
  import Banner from '../parts/Banner.svelte';
  import ContactsSection from '../parts/ContactsSection.svelte';

  const route = currentRoute();
  // One field again: the COMPANY's number. The user's own number was given once
  // at first launch and lives in Settings — the verification SMS goes there.
  let companyNumber = $state(route.name === 'add' && route.phone !== undefined ? route.phone : '');
  let code = $state('');
  let flow = $state<AddContactState>(addContactInitialState());
  let busy = $state<'looking_up' | 'verifying_code' | undefined>(undefined);
  let ownAsCompany = $state(false);

  async function begin(): Promise<void> {
    ownAsCompany = false;
    const ownNumber = (await getSetting<string>(SETTING_USER_PHONE)) ?? '';
    if (normalizeNumber(companyNumber) === ownNumber) {
      ownAsCompany = true;
      return;
    }
    busy = 'looking_up';
    flow = await startAddContact(companyNumber, ownNumber);
    busy = undefined;
  }

  async function verify(): Promise<void> {
    busy = 'verifying_code';
    flow = await confirmAddContact(flow, code);
    busy = undefined;
    if (flow.stage === 'done' && flow.entry !== undefined) {
      // Now there is something worth protecting; browsers grant this readily to
      // an installed app.
      try {
        await navigator.storage.persist();
      } catch {
        // Nothing to do — eviction stays survivable by design.
      }
      const accountId = flow.entry.accountId;
      void syncContact(accountId);
      // The conversation we land in shows the notification nudge once — adding
      // a company is the moment the user most wants its replies to reach them.
      markFreshAdd();
      navigate({ name: 'conversation', accountId });
    }
  }

  function errorText(state: AddContactState): string | undefined {
    if (state.error === undefined) {
      return undefined;
    }
    const key = `add.error.${state.error}`;
    const known: Record<string, string> = {
      'add.error.bad_request': t('add.error.bad_request'),
      'add.error.not_found': t('add.error.not_found'),
      'add.error.entry_withdrawn': t('add.error.entry_withdrawn'),
      'add.error.number_not_allowed': t('add.error.number_not_allowed'),
      'add.error.rate_limited': t('add.error.rate_limited'),
      'add.error.invalid_code': t('add.error.invalid_code'),
      'add.error.code_expired': t('add.error.code_expired'),
      'add.error.no_network': t('add.error.no_network'),
      'add.error.clock_unreliable': t('add.error.clock_unreliable'),
    };
    if (key in known) {
      return known[key];
    }
    if (
      state.error === 'manifest_expired' ||
      state.error === 'manifest_bad_signature' ||
      state.error === 'manifest_too_old' ||
      state.error === 'directory_not_configured'
    ) {
      return t('add.reducedTrust');
    }
    return t('add.error.generic');
  }

  const error = $derived(errorText(flow));
  const progressKey = $derived(
    busy === undefined ? undefined : (`add.progress.${busy}` as keyof Dict),
  );
</script>

<header class="bar">
  <button class="back" onclick={goBack} aria-label={t('common.back')}>‹</button>
  <h1>{t('add.title')}</h1>
</header>

<div class="body">
  {#if flow.stage !== 'awaiting_code'}
    <label for="company-number">{t('add.numberLabel')}</label>
    <input
      id="company-number"
      type="tel"
      inputmode="tel"
      autocomplete="off"
      placeholder="+48 …"
      bind:value={companyNumber}
      oninput={() => (ownAsCompany = false)}
      onkeydown={(event) => {
        if (event.key === 'Enter') void begin();
      }}
    />
    <p class="hint">{t('add.numberHint')}</p>
    {#if ownAsCompany}
      <Banner kind="warn" text={t('add.error.own_as_company')} />
    {/if}
    {#if error !== undefined}
      <Banner kind="warn" text={error} />
    {/if}
    <button
      class="primary"
      disabled={busy !== undefined || companyNumber.trim().length === 0}
      onclick={() => void begin()}
    >
      {progressKey !== undefined ? t(progressKey) : t('add.continue')}
    </button>
    <hr class="divider" />
    <ContactsSection
      onpick={(phone) => {
        companyNumber = phone;
        ownAsCompany = false;
        void begin();
      }}
    />
  {:else}
    <h2>{t('add.codeTitle', { phone: formatPhone(flow.ownPhone ?? '') })}</h2>
    <p class="hint">{t('add.codeHint')}</p>
    <input
      id="code"
      inputmode="numeric"
      autocomplete="one-time-code"
      maxlength="8"
      placeholder="000000"
      bind:value={code}
      onkeydown={(event) => {
        if (event.key === 'Enter') void verify();
      }}
    />
    {#if error !== undefined}
      <Banner kind="warn" text={error} />
    {/if}
    <button
      class="primary"
      disabled={busy !== undefined || code.trim().length < 4}
      onclick={() => void verify()}
    >
      {busy === 'verifying_code' ? t('add.progress.verifying_code') : t('add.verify')}
    </button>
  {/if}
</div>

<style>
  .bar {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 10px 12px;
  }
  .back {
    border: 0;
    background: transparent;
    font-size: 28px;
    cursor: pointer;
    color: var(--ink);
    padding: 0 8px;
  }
  h1 {
    margin: 0;
    font-size: 20px;
  }
  .body {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }
  .divider {
    border: 0;
    border-top: 1px solid var(--line);
    margin: 20px 0 12px;
    width: 100%;
  }
  h2 {
    margin: 0;
    font-size: 18px;
  }
  label {
    font-size: 14px;
    color: var(--ink-soft);
  }
  input {
    border: 1px solid var(--line);
    border-radius: 10px;
    padding: 14px;
    font-size: 20px;
    background: var(--surface);
    color: var(--ink);
    letter-spacing: 0.06em;
  }
  input:focus {
    outline: 2px solid var(--brand);
    border-color: transparent;
  }
  .hint {
    margin: 0;
    font-size: 13px;
    color: var(--ink-soft);
  }
  .primary {
    margin-top: 12px;
    border: 0;
    border-radius: 999px;
    background: var(--brand);
    color: var(--brand-ink);
    font-size: 16px;
    font-weight: 600;
    padding: 14px;
    cursor: pointer;
  }
  .primary:disabled {
    opacity: 0.5;
  }
</style>
